<template>
    <form-view class="custom-api-sandbox-view"
        @submitsuccess="showTestResult">
        <veui-steps :steps="steps" :current="2"/>
        <h3>HTTP请求</h3>
        <template slot="field">
            <veui-field label="地址：">
                <veui-span>{{ urlText }}</veui-span>
            </veui-field>
            <veui-fieldset label="参数列表：">
                <veui-table class="parameter-table" :data="parameters" ui="slim">
                    <veui-table-column field="name" title="参数名">
                        <template slot-scope="props"><span class="table-multi-line">{{ props.name }}</span></template>
                    </veui-table-column>
                    <veui-table-column field="defaultValue" title="默认值">
                        <template slot-scope="props"><span class="table-multi-line">{{ props.defaultValue }}</span></template>
                    </veui-table-column>
                    <veui-table-column field="required" title="必填" width="100">
                        <template slot-scope="props"><span class="table-multi-line">{{ props.required | booleanText }}</span></template>
                    </veui-table-column>
                    <veui-table-column field="testValue" title="测试值">
                        <template slot-scope="props">
                            <veui-span v-if="isDetail" class="table-multi-line">{{ props.testValue }}</veui-span>
                            <veui-field v-else
                                :name="`${props.name}${props.index}`"
                                :rules="props.required ? 'required' : null"
                                :field="`${props.name}${props.index}`">
                                <veui-input v-model="store.formData.parameters[props.index].testValue"/>
                            </veui-field>
                        </template>
                    </veui-table-column>
                </veui-table>
            </veui-fieldset>
            <veui-field v-if="!isDetail">
                <veui-button class="primary-border-btn"
                    ui="small"
                    type="submit"
                    @click="testApi">功能测试</veui-button>
                <veui-span class="result">测试结果：
                    <veui-span class="success" v-if="isPassed">通过</veui-span>
                    <veui-span class="error" v-if="isFailed">失败</veui-span>
                </veui-span>
                <veui-span class="reason" v-if="isFailed">失败原因：{{failedReason}}</veui-span>
            </veui-field>
        </template>
        <template slot="operation">
            <veui-button ui="primary"
                v-if="showBackApiBtn"
                @click="backToApiList">返回API列表</veui-button>
            <veui-button v-if="!this.isDetail" @click="genJsonFile">生成json数据</veui-button>
            <veui-button @click="back">返回上一步</veui-button>
        </template>
    </form-view>
</template>

<script>
import qs from 'qs';
import {get, map} from 'lodash';
import FormModel from './SandboxFormModel';
import FormView from '@baidu/ve-ria/views/FormView';
import postmanJSONSchema from './postmanSchema';
import createView from '@baidu/ve-ria/views/createView';
import pageTypeMixin from '@/common/mixins/pageType';
import {Steps, Field, Fieldset, Button, Input, Span, Dialog, Table, Column} from 'veui';

export default createView(FormView, FormModel, {
    name: 'SandboxFormView',
    components: {
        'veui-field': Field,
        'veui-fieldset': Fieldset,
        'veui-input': Input,
        'veui-span': Span,
        'veui-button': Button,
        'veui-steps': Steps,
        'veui-dialog': Dialog,
        'veui-table': Table,
        'veui-table-column': Column
    },
    computed: {
        parameters() {
            return this.store.formData.parameters || [];
        },
        methodText() {
            return this.store.methodMap[get(this.store.apiDetail, 'requestMethod')];
        },
        urlText() {
            if (this.store.apiDetail) {
                return `${this.methodText} ${this.store.proxyUrlPrefix}`;
            }
            return '';
        },
        showBackApiBtn() {
            return (this.isPassed && !this.isDetail) || this.isDetail;
        }
    },
    created() {
        this.store.requestApiDetail();
    },
    data() {
        return {
            steps: [
                {label: '新建API'},
                {label: '沙盒测试'},
                {label: '提交审核'}
            ],
            isPassed: false,
            isFailed: false,
            failedReason: null
        };
    },
    mixins: [pageTypeMixin],
    mounted() {
        if (this.isDetail) {
            this.steps.splice(2, 1); // 去除提交审核step
        }
    },
    methods: {
        genJsonFile() {
            const link = document.createElement('a');
            link.download = 'postman接口配置文件.json';
            link.style.display = 'none';
            // 字符内容转变成blob地址
            const blob = new Blob(this.fillDataToSchema());
            link.href = URL.createObjectURL(blob);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        fillDataToSchema() {
            let apiDetail = this.store.apiDetail;
            let instance = postmanJSONSchema.item[0];
            let fullUrl = this.genRawUrl({appid: apiDetail.appId, urlId: apiDetail.proxyUrl});
            let [url, query] = fullUrl.split('?');
            let method = this.store.methodMap[apiDetail.requestMethod];
            let isPost = method.toUpperCase() === 'POST';
            let params = map(
                this.parameters,
                ({name, testValue, defaultValue}) => ({key: name, value: testValue || defaultValue || ''})
            );
            let originHeader = apiDetail.corsDomainWhiteList
                ? [{
                    key: 'Origin',
                    value: apiDetail.corsDomainWhiteList
                }]
                : [];
            instance.name = apiDetail.name;
            instance.request = {
                method: method.toLowerCase(),
                body: {
                    'mode': 'urlencoded',
                    'urlencoded': isPost ? [
                        {
                            'type': 'text',
                            'description': '',
                            'key': 'contentType',
                            'value': this.store.contentTypeMap[apiDetail.contentType]
                        },
                        ...params
                    ] : []
                },
                header: [
                    {
                        'key': 'ContentType',
                        'value': 'application/x-www-form-urlencoded'
                    },
                    ...originHeader
                ],
                url: {
                    ... instance.request.url,
                    raw: url,
                    host: (process.env.VUE_APP_MUSES_SANDBOX_URL || '').split('.'),
                    protocol: this.store.protocolMap[apiDetail.protocol].toLowerCase(),
                    query: [
                        ...map(qs.parse(query), (value, key) => ({key, value})),
                        ...params
                    ]
                }
            };
            return [JSON.stringify(postmanJSONSchema, null, 4)];
        },
        genRawUrl(config, query) {
            let {appid, urlId} = config;
            let os = {
                '__osInfoParam__': JSON.stringify({
                    mcid: '',
                    query: '',
                    searchTime: Date.now()
                })
            };
            let apiKey = `${appid}_${urlId.replace(/^\//, '').replace(/\?.*$/, '')}`;
            return `${this.store.proxyUrlPrefix}?` + decodeURIComponent(qs.stringify({
                apiKey,
                ...os,
                ...query,
                '__osJsonMode__': 1
            }));
        },
        backToApiList() {
            this.global.redirect({
                url: this.store.getApiListUrl(),
                type: 'push'
            });
        },
        testApi() {
            // 设置formData，validator检查用
            this.store.formData.parameters.forEach(
                (param, index) => this.store.formData[`${param.name}${index}`] = param.testValue
            );
            // 重置
            this.isPassed = false;
            this.isFailed = false;
        },
        showTestResult(res) {
            if (res.success) {
                this.isPassed = true;
            }
            else {
                this.isFailed = true;
                this.failedReason = res.reason;
            }
        }
    }
});
</script>

<style lang="less">
@import '~veui-theme-one/variables.less';

.custom-api-sandbox-view {
    h3 {
        color: @veui-text-color-strong;
    }
    .parameter-table {
        display: inline-table;
    }
    .veui-field-no-label {
        &:before {
            display: none;
        }
    }
    .veui-span {
        .success {
            color: @veui-success-color;
        }
        .error {
            color: @veui-error-color;
        }
        &.result,
        &.reason {
            margin-left: 50px;
        }
    }
}
</style>
